import "./Payments.css";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuthStore } from "../../../Services/authStore";
import {
  toastSuccess,
  toastError,
  toastWarning,
} from "../../../Services/toaster";
import editIcon from "../../../assets/icons/edit.png";
import deleteIcon from "../../../assets/icons/delete.png";
import BackIcon from "../../../assets/icons/arrow.png";
import PaymentApprovals from "./PaymentApprovals";

// ─────────────────────────────────────────────────────────
//  CONSTANTS
// ─────────────────────────────────────────────────────────
const CURRENCY_OPTIONS = ["INR", "USD", "EUR", "GBP"];
const PAYMENT_METHODS = [
  "Bank Transfer",
  "Cheque",
  "Cash",
  "Credit Card",
  "UPI",
  "Net Banking",
];
const PAYMENT_STATUS_OPTIONS = [
  "Draft",
  "Submitted",
  "Approved",
  "Paid",
  "Cancelled",
];

const EMPTY_FORM = {
  invoice_id: "",
  invoice_number: "",
  po_id: "",
  po_number: "",
  payment_number: "",
  payment_date: "",
  payment_amount: "",
  payment_amount_fc: 0,
  payment_method: "",
  payment_reference: "",
  currency: "INR",
  currency_rate: 1,
  payment_status: "Draft",
  remarks: "",
  approvals: [],
};

// ─────────────────────────────────────────────────────────
//  PAYMENTS VIEW
// ─────────────────────────────────────────────────────────
function PaymentsView() {
  // ─── State ──────────────────────────────────────────────
  const [showForm, setShowForm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState("");

  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  // ─── Dropdown Data ──────────────────────────────────────
  const [invoices, setInvoices] = useState([]);
  const [loadingDropdowns, setLoadingDropdowns] = useState(false);
  const [dropdownsLoaded, setDropdownsLoaded] = useState(false);

  // ─── Invoice Balance (total / paid / remaining) ─────────
  const [invoiceBalance, setInvoiceBalance] = useState(null);
  // shape: { total, paid, remaining }

  const token = useAuthStore((state) => state.token);
  const API_URL = process.env.REACT_APP_API_URL;

  // ─── Helper: Paid Amount for an Invoice ────────────────
  // Must live INSIDE the component because it reads the `payments` state.
  const getInvoicePaidAmount = useCallback(
    (invoiceId, excludePaymentId = null) => {
      return payments
        .filter(
          (p) =>
            p.invoice_id === parseInt(invoiceId) &&
            p.payment_status !== "Cancelled" &&
            p.id !== excludePaymentId, // don't double-count the payment being edited
        )
        .reduce((sum, p) => sum + (parseFloat(p.payment_amount) || 0), 0);
    },
    [payments],
  );

  // ─── API Calls ──────────────────────────────────────────

  // GET /api/payment/ - Get All Payments
  const fetchPayments = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/payment/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          accept: "application/json",
        },
        params: { skip: 0, limit: 100 },
      });
      const data = response.data?.data || response.data || [];
      setPayments(data);
    } catch (err) {
      console.error("Failed to load payments:", err.response?.data);
      toastError("Failed to load payments");
    } finally {
      setLoading(false);
    }
  }, [API_URL, token]);

  // GET /api/payment/{payment_id} - Get Payment Details
  const fetchPaymentById = useCallback(
    async (id) => {
      try {
        const response = await axios.get(`${API_URL}/api/payment/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
          },
        });
        return response.data?.data || response.data;
      } catch (err) {
        console.error("Failed to fetch payment:", err.response?.data);
        throw err;
      }
    },
    [API_URL, token],
  );

  // ─── Fetch Approval Document for Payment ───────────────
  const fetchApprovalDocument = useCallback(async () => {
    try {
      const documentCode = "PYMT";

      const response = await axios.get(
        `${API_URL}/approval/document/${documentCode}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
          },
        },
      );

      const approvalData = response.data?.data || [];

      return approvalData.map((item) => ({
        approval_level: item.approval_level,
        approver_user_id: item.approver_user_id,
        status: "Pending",
        remarks: "",
      }));
    } catch (err) {
      console.error(
        "Failed to fetch payment approval document:",
        err.response?.data || err,
      );
      return [];
    }
  }, [API_URL, token]);

  // ─── Fetch Dropdown Data ──────────────────────────────

  // GET /api/invoice/ - Get Invoices with full details
  const fetchInvoices = useCallback(async () => {
    try {
      // First, get the list of all invoices
      const response = await axios.get(`${API_URL}/api/invoice/`, {
        headers: {
          Authorization: `Bearer ${token}`,
          accept: "application/json",
        },
        params: { skip: 0, limit: 100 },
      });
      const data = response.data?.data || response.data || [];
      console.log("Raw Invoice data:", data);

      // Filter invoices that are not Draft or Cancelled (ready for payment)
      // This includes: Submitted, Approved, Paid
      const readyInvoices = data.filter(
        (inv) =>
          inv.invoice_status !== "Draft" && inv.invoice_status !== "Cancelled",
      );

      console.log(
        "Ready Invoices (excluding Draft and Cancelled):",
        readyInvoices,
      );

      // Fetch full details for each ready invoice to get all data
      const fullInvoices = await Promise.all(
        readyInvoices.map(async (inv) => {
          try {
            // Use the GET /api/invoice/{invoice_id} endpoint
            const detailResponse = await axios.get(
              `${API_URL}/api/invoice/${inv.id}`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  accept: "application/json",
                },
              },
            );
            const invoiceDetail =
              detailResponse.data?.data || detailResponse.data;
            console.log(`Invoice ${inv.id} details:`, invoiceDetail);
            return invoiceDetail;
          } catch (err) {
            console.error(`Failed to fetch invoice ${inv.id} details:`, err);
            return inv; // Return basic data if detail fetch fails
          }
        }),
      );

      console.log("Full Invoice data:", fullInvoices);
      setInvoices(fullInvoices);
    } catch (err) {
      console.error("Failed to load invoices:", err);
      toastError("Failed to load invoices");
    }
  }, [API_URL, token]);

  const fetchDropdownData = useCallback(async () => {
    if (dropdownsLoaded) return;

    setLoadingDropdowns(true);
    try {
      await fetchInvoices();
      setDropdownsLoaded(true);
    } catch (err) {
      console.error("Failed to load dropdown data:", err);
      toastError("Failed to load form data");
    } finally {
      setLoadingDropdowns(false);
    }
  }, [fetchInvoices, dropdownsLoaded]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  // ─── CRUD Operations ────────────────────────────────────

  // POST /api/payment/create - Create Payment
  const createPayment = async (paymentData) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/payment/create`,
        paymentData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );
      return response.data;
    } catch (err) {
      console.error("Create payment error:", err.response?.data);
      throw err;
    }
  };

  // PUT /api/payment/{payment_id}/update - Update Payment
  const updatePayment = async (id, paymentData) => {
    try {
      const response = await axios.put(
        `${API_URL}/api/payment/${id}/update`,
        paymentData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );
      return response.data;
    } catch (err) {
      console.error("Update payment error:", err.response?.data);
      throw err;
    }
  };

  // DELETE /api/payment/{payment_id}/delete - Delete Payment
  const deletePayment = async (id) => {
    try {
      const response = await axios.delete(
        `${API_URL}/api/payment/${id}/delete`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
          },
        },
      );
      return response.data;
    } catch (err) {
      console.error("Delete payment error:", err.response?.data);
      throw err;
    }
  };

  // ─── Form Handlers ──────────────────────────────────────

  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const openAdd = async () => {
    setIsEditMode(false);
    setEditingId(null);
    setFormError("");
    setShowForm(true);
    setFormLoading(true);
    setInvoiceBalance(null);

    try {
      // Refresh payments so paid-so-far totals are current
      await fetchPayments();
      await fetchDropdownData();

      // Fetch approval document for Payment
      const approvals = await fetchApprovalDocument();

      setForm({
        ...EMPTY_FORM,
        payment_date: new Date().toISOString().split("T")[0],
        payment_number: "",
        approvals: approvals,
      });
    } catch (err) {
      setForm({
        ...EMPTY_FORM,
        payment_date: new Date().toISOString().split("T")[0],
        payment_number: "",
        approvals: [],
      });
    } finally {
      setFormLoading(false);
    }
  };

  const openEdit = async (payment) => {
    // Refresh payments so paid-so-far totals are current
    await fetchPayments();
    await fetchDropdownData();

    setFormError("");
    setIsEditMode(true);
    setEditingId(payment.id);
    setFormLoading(true);
    setShowForm(true);
    setInvoiceBalance(null);

    try {
      const data = await fetchPaymentById(payment.id);
      setForm({
        invoice_id: data.invoice_id || "",
        invoice_number: data.invoice_number || "",
        po_id: data.po_id || "",
        po_number: data.po_number || "",
        payment_number: data.payment_number || "",
        payment_date: data.payment_date || "",
        payment_amount: data.payment_amount || "",
        payment_amount_fc: data.payment_amount_fc || 0,
        payment_method: data.payment_method || "",
        payment_reference: data.payment_reference || "",
        currency: data.currency || "INR",
        currency_rate: data.currency_rate || 1,
        payment_status: data.payment_status || "Draft",
        remarks: data.remarks || "",
        approvals: data.approvals || [],
      });

      // Compute balance for the invoice this payment belongs to,
      // excluding this payment itself from the "already paid" sum.
      const matchedInvoice = invoices.find((inv) => inv.id === data.invoice_id);
      const total = matchedInvoice
        ? parseFloat(matchedInvoice.invoice_total) || 0
        : parseFloat(data.invoice_total) || 0;
      const paidSoFar = getInvoicePaidAmount(data.invoice_id, data.id);
      setInvoiceBalance({
        total,
        paid: paidSoFar,
        remaining: total - paidSoFar,
      });
    } catch (err) {
      setFormError("Failed to load payment details.");
      toastError("Failed to load payment details.");
    } finally {
      setFormLoading(false);
    }
  };

  const closeForm = () => {
    setShowForm(false);
    setForm({ ...EMPTY_FORM });
    setFormError("");
    setIsEditMode(false);
    setEditingId(null);
    setInvoiceBalance(null);
  };

  // ─── Handle Invoice Selection ──────────────────────────

  const handleInvoiceSelection = (selectedValue) => {
    if (!selectedValue) {
      setF("invoice_id", "");
      setF("invoice_number", "");
      setF("po_id", "");
      setF("po_number", "");
      setF("currency", "INR");
      setF("currency_rate", 1);
      setInvoiceBalance(null);
      return;
    }

    const invoice = invoices.find((inv) => inv.id === parseInt(selectedValue));

    if (invoice) {
      setF("invoice_id", selectedValue);
      setF("invoice_number", invoice.invoice_number || "");
      setF("po_id", invoice.po_id || "");
      setF("po_number", invoice.po_number || "");
      setF("currency", invoice.currency || "INR");
      setF("currency_rate", invoice.currency_rate || 1);

      const invoiceTotal = parseFloat(invoice.invoice_total) || 0;
      const paidSoFar = getInvoicePaidAmount(
        selectedValue,
        isEditMode ? editingId : null,
      );
      const remaining = invoiceTotal - paidSoFar;
      setInvoiceBalance({ total: invoiceTotal, paid: paidSoFar, remaining });

      // Auto-fill with what's left, not the full invoice total
      if (!form.payment_amount || form.payment_amount <= 0) {
        setF("payment_amount", remaining > 0 ? remaining : 0);
      }

      if (remaining <= 0) {
        toastWarning(`This invoice is already fully paid.`);
      } else {
        toastSuccess(`Selected invoice ${invoice.invoice_number}`);
      }
    } else {
      toastError("Invoice not found");
      setF("invoice_id", "");
      setF("invoice_number", "");
      setF("po_id", "");
      setF("po_number", "");
      setInvoiceBalance(null);
    }
  };

  // ─── Submit Handlers ────────────────────────────────────

  const handleSubmit = async (status = "Draft") => {
    setFormError("");

    if (!form.invoice_id) {
      setFormError("Invoice is required.");
      toastWarning("Invoice is required.");
      return;
    }
    if (!form.payment_date) {
      setFormError("Payment date is required.");
      toastWarning("Payment date is required.");
      return;
    }
    if (!form.payment_amount || form.payment_amount <= 0) {
      setFormError("Valid payment amount is required.");
      toastWarning("Valid payment amount is required.");
      return;
    }
    if (!form.payment_method) {
      setFormError("Payment method is required.");
      toastWarning("Payment method is required.");
      return;
    }

    // Validate payment amount doesn't exceed the REMAINING invoice balance
    // (invoice total minus payments already made against it)
    const selectedInvoice = invoices.find(
      (inv) => inv.id === parseInt(form.invoice_id),
    );
    if (selectedInvoice) {
      const invoiceTotal = parseFloat(selectedInvoice.invoice_total) || 0;
      const paidSoFar = getInvoicePaidAmount(
        form.invoice_id,
        isEditMode ? editingId : null,
      );
      const remaining = invoiceTotal - paidSoFar;
      const paymentAmount = parseFloat(form.payment_amount) || 0;

      if (paymentAmount > remaining) {
        setFormError(
          `Payment amount (${formatCurrency(
            paymentAmount,
            form.currency,
          )}) exceeds remaining balance (${formatCurrency(
            remaining,
            form.currency,
          )}). Already paid ${formatCurrency(
            paidSoFar,
            form.currency,
          )} of ${formatCurrency(invoiceTotal, form.currency)}.`,
        );

        toastWarning(
          `Payment amount cannot exceed remaining balance (${formatCurrency(
            remaining,
            form.currency,
          )}).`,
        );
        return;
      }
    }

    const payload = {
      ...form,
      payment_status: status,
      payment_number: form.payment_number || "",
      approvals: Array.isArray(form.approvals) ? form.approvals : [],
    };

    // Ensure correct types
    payload.invoice_id = parseInt(payload.invoice_id) || 0;
    payload.po_id = parseInt(payload.po_id) || 0;
    payload.payment_amount = parseFloat(payload.payment_amount) || 0;
    payload.payment_amount_fc = parseFloat(payload.payment_amount) || 0;
    payload.currency_rate = parseFloat(payload.currency_rate) || 1;

    setFormLoading(true);

    try {
      if (isEditMode) {
        await updatePayment(editingId, payload);
        toastSuccess("Payment updated successfully.");
      } else {
        const response = await createPayment(payload);
        toastSuccess(
          `Payment created: ${response.payment_number || "Successfully"}`,
        );
      }
      closeForm();
      fetchPayments();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => `${d.loc?.join(".")} — ${d.msg}`).join("\n")
        : detail || "Failed to save payment.";
      setFormError(msg);
      toastError(msg);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this payment?")) {
      return;
    }

    try {
      await deletePayment(id);
      toastSuccess("Payment deleted successfully.");
      fetchPayments();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toastError(detail || "Failed to delete payment.");
    }
  };

  // ── Filters ─────────────────────────────────────────────

  const filteredPayments = payments.filter((payment) => {
    const matchStatus =
      statusFilter === "All" || payment.payment_status === statusFilter;
    const matchSearch =
      (payment.payment_number || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (payment.invoice_number || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (payment.po_number || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (payment.payment_method || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
    return matchStatus && matchSearch;
  });

  // ── Format Helpers ──────────────────────────────────────

  const formatCurrency = (amount, currency = "USD") => {
    if (amount === "" || amount === null || amount === undefined) {
      return "";
    }

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(amount));
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusClass = (status) => {
    const map = {
      Draft: "pay-status-draft",
      Submitted: "pay-status-submitted",
      Approved: "pay-status-approved",
      Paid: "pay-status-paid",
      Cancelled: "pay-status-cancelled",
    };
    return map[status] || "pay-status-draft";
  };

  // ─── Approval status badge class ───────────────────────
  const getApprovalStatusClass = (status) => {
    const map = {
      Pending: "pay-approval-pending",
      Approved: "pay-approval-approved",
      Rejected: "pay-approval-rejected",
    };
    return map[status] || "pay-approval-pending";
  };

  // ─── Form View ──────────────────────────────────────────

  if (showForm) {
    return (
      <div className="pay-form-page">
        <div className="pay-form-header">
          <div className="pay-form-header-left">
            <button className="pay-back-btn" onClick={closeForm}>
              <img src={BackIcon} alt="Back" className="pay-back-icon" />
            </button>
            <div>
              <h2 className="pay-form-title">
                {isEditMode ? "Edit Payment" : "New Payment"}
              </h2>
              <p className="pay-form-subtitle">
                {isEditMode ? "Update payment details" : "Create a new payment"}
              </p>
            </div>
          </div>
        </div>

        <div className="pay-form-card">
          {loadingDropdowns && (
            <p style={{ color: "#9F8E87", fontSize: 13, marginBottom: 14 }}>
              Loading form data...
            </p>
          )}
          {formLoading && (
            <p style={{ color: "#9F8E87", fontSize: 13, marginBottom: 14 }}>
              {isEditMode ? "Loading..." : "Saving..."}
            </p>
          )}
          {formError && <p className="pay-form-error">{formError}</p>}

          <div className="pay-form-section">
            <h3>Payment Information</h3>
            <div className="pay-form-grid">
              <div className="pay-form-group">
                <label>Payment Number</label>
                <input
                  value={
                    isEditMode ? form.payment_number || "N/A" : "Auto-generated"
                  }
                  disabled
                  style={{
                    background: "#f5f5f5",
                    fontWeight: isEditMode ? "600" : "400",
                    color: isEditMode ? "#2A070C" : "#A27B5B",
                  }}
                />
                {!isEditMode && (
                  <div className="pay-sequence-info">
                    <span className="pay-sequence-label">
                      Payment number will be auto-generated
                    </span>
                  </div>
                )}
              </div>
              <div className="pay-form-group">
                <label>Invoice *</label>
                <select
                  name="invoice_id"
                  value={form.invoice_id}
                  onChange={(e) => handleInvoiceSelection(e.target.value)}
                  disabled={loadingDropdowns || isEditMode}
                >
                  <option value="">Select Invoice</option>
                  {invoices.length === 0 ? (
                    <option value="" disabled>
                      No invoices available for payment
                    </option>
                  ) : (
                    invoices.map((inv) => (
                      <option key={inv.id} value={inv.id}>
                        {inv.invoice_number} - {inv.vendor_name || "Vendor"} (
                        {formatCurrency(
                          inv.invoice_total || 0,
                          inv.currency || "INR",
                        )}{" "}
                        {inv.invoice_status}
                      </option>
                    ))
                  )}
                </select>
                {invoices.length === 0 && !loadingDropdowns && (
                  <div
                    className="pay-form-hint"
                    style={{
                      color: "#B7950B",
                      fontSize: "12px",
                      marginTop: "4px",
                    }}
                  >
                    No invoices available for payment. Invoices must be
                    Submitted, Approved, or Paid status.
                  </div>
                )}
              </div>
              <div className="pay-form-group">
                <label>Invoice Number</label>
                <input
                  value={form.invoice_number}
                  disabled
                  style={{ background: "#f5f5f5" }}
                />
              </div>
              <div className="pay-form-group">
                <label>PO Number</label>
                <input
                  value={form.po_number}
                  disabled
                  style={{ background: "#f5f5f5" }}
                />
              </div>
              <div className="pay-form-group">
                <label>Payment Date *</label>
                <input
                  name="payment_date"
                  type="date"
                  value={form.payment_date}
                  onChange={(e) => setF("payment_date", e.target.value)}
                />
              </div>

              {/* Single Payment Amount field — validated against remaining balance */}
              <div className="pay-form-group">
                <div className="pay-form-group">
                  <label>Payment Amount *</label>

                  <input
                    name="payment_amount"
                    type="text"
                    inputMode="decimal"
                    value={
                      form.payment_amount === "" ||
                      form.payment_amount === null ||
                      form.payment_amount === undefined
                        ? ""
                        : Number(form.payment_amount).toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                    }
                    onChange={(e) => {
                      // Remove commas before saving the value
                      const raw = e.target.value.replace(/,/g, "");

                      // Allow only numbers with maximum 2 decimal places
                      if (raw === "" || /^\d*(\.\d{0,2})?$/.test(raw)) {
                        const val = parseFloat(raw) || 0;

                        if (invoiceBalance && val > invoiceBalance.remaining) {
                          toastWarning(
                            `Payment amount cannot exceed remaining balance (${formatCurrency(
                              invoiceBalance.remaining,
                              form.currency,
                            )})`,
                          );
                          return;
                        }

                        // Store raw numeric value without commas
                        setF("payment_amount", raw);
                      }
                    }}
                    onBlur={() => {
                      if (form.payment_amount !== "") {
                        setF(
                          "payment_amount",
                          Number(
                            String(form.payment_amount).replace(/,/g, ""),
                          ).toFixed(2),
                        );
                      }
                    }}
                    placeholder="0.00"
                  />

                  {form.invoice_id && invoiceBalance && (
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#9F8E87",
                        marginTop: "3px",
                      }}
                    >
                      Invoice Total:{" "}
                      {formatCurrency(invoiceBalance.total, form.currency)} ·
                      Remaining:{" "}
                      <strong>
                        {formatCurrency(
                          invoiceBalance.remaining,
                          form.currency,
                        )}
                      </strong>
                    </div>
                  )}
                </div>
              </div>

              <div className="pay-form-group">
                <label>Payment Method *</label>
                <select
                  name="payment_method"
                  value={form.payment_method}
                  onChange={(e) => setF("payment_method", e.target.value)}
                  disabled={loadingDropdowns}
                >
                  <option value="">Select Payment Method</option>
                  {PAYMENT_METHODS.map((method) => (
                    <option key={method} value={method}>
                      {method}
                    </option>
                  ))}
                </select>
              </div>
              <div className="pay-form-group">
                <label>Payment Reference</label>
                <input
                  name="payment_reference"
                  value={form.payment_reference}
                  onChange={(e) => setF("payment_reference", e.target.value)}
                  placeholder="Transaction/Cheque reference"
                />
              </div>
              <div className="pay-form-group">
                <label>Currency</label>
                <select
                  name="currency"
                  value={form.currency}
                  onChange={(e) => setF("currency", e.target.value)}
                >
                  {CURRENCY_OPTIONS.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div className="pay-form-group">
                <label>Currency Rate</label>
                <input
                  name="currency_rate"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.currency_rate}
                  onChange={(e) => setF("currency_rate", e.target.value)}
                />
              </div>
              <div className="pay-form-group">
                <label>Status</label>
                <select
                  name="payment_status"
                  value={form.payment_status}
                  onChange={(e) => setF("payment_status", e.target.value)}
                  disabled={!isEditMode}
                >
                  {PAYMENT_STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="pay-form-grid">
            <div className="pay-form-group pay-col-full">
              <label>Remarks</label>
              <textarea
                rows="3"
                value={form.remarks}
                onChange={(e) => setF("remarks", e.target.value)}
                placeholder="Additional remarks..."
                disabled={loadingDropdowns}
              />
            </div>
          </div>

          <div className="pay-form-footer">
            <button className="pay-btn pay-btn-secondary" onClick={closeForm}>
              Cancel
            </button>
            {isEditMode && (
              <>
                <button
                  className="pay-btn pay-btn-draft"
                  onClick={() => handleSubmit("Draft")}
                  disabled={formLoading || loadingDropdowns}
                >
                  Save Draft
                </button>
                <button
                  className="pay-btn pay-btn-primary"
                  onClick={() => handleSubmit("Submitted")}
                  disabled={formLoading || loadingDropdowns}
                >
                  {formLoading ? "Saving..." : "Submit"}
                </button>
              </>
            )}
            {!isEditMode && (
              <>
                <button
                  className="pay-btn pay-btn-draft"
                  onClick={() => handleSubmit("Draft")}
                  disabled={formLoading || loadingDropdowns}
                >
                  Save Draft
                </button>
                <button
                  className="pay-btn pay-btn-primary"
                  onClick={() => handleSubmit("Submitted")}
                  disabled={formLoading || loadingDropdowns}
                >
                  {formLoading ? "Creating..." : "Submit"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ─── List View ──────────────────────────────────────────

  return (
    <div className="pay-list-view">
      <div className="pay-page-header">
        <div className="pay-page-header-left">
          <h1 className="pay-page-title">Payments</h1>
          <p className="pay-page-subtitle">Manage payments and approvals</p>
        </div>
        <div className="pay-page-header-right">
          <div className="pay-search-box">
            <span className="pay-search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search payments..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            className="pay-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="All">All Status</option>
            <option value="Draft">Draft</option>
            <option value="Submitted">Submitted</option>
            <option value="Approved">Approved</option>
            <option value="Paid">Paid</option>
            <option value="Cancelled">Cancelled</option>
          </select>
          <button className="pay-btn pay-btn-primary" onClick={openAdd}>
            + Create Payment
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="pay-table-card">
        <div className="pay-table-responsive">
          <table className="pay-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Payment Number</th>
                <th>Invoice</th>
                <th>PO Number</th>
                <th>Date</th>
                <th>Amount</th>
                <th>Method</th>
                <th>Payment Status</th>
                <th>Approval Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10" className="pay-empty-row">
                    Loading...
                  </td>
                </tr>
              ) : filteredPayments.length === 0 ? (
                <tr>
                  <td colSpan="10">
                    <div className="pay-empty-state">
                      <div className="pay-empty-icon">💳</div>
                      <div className="pay-empty-title">No Payments Found</div>
                      <div className="pay-empty-desc">
                        {searchTerm
                          ? `No results for "${searchTerm}"`
                          : "Create your first payment"}
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredPayments.map((payment, index) => (
                  <tr key={payment.id}>
                    <td>{index + 1}</td>
                    <td>
                      <span className="pay-number-badge">
                        {payment.payment_number}
                      </span>
                    </td>
                    <td>
                      <span className="pay-invoice-number">
                        {payment.invoice_number || "-"}
                      </span>
                    </td>
                    <td>
                      <span className="pay-po-number">
                        {payment.po_number || "-"}
                      </span>
                    </td>
                    <td>{formatDate(payment.payment_date)}</td>
                    <td>
                      {formatCurrency(
                        payment.payment_amount,
                        payment.currency || "INR",
                      )}
                    </td>
                    <td>
                      <span className="pay-method-badge">
                        {payment.payment_method || "-"}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`pay-status-badge ${getStatusClass(payment.payment_status)}`}
                      >
                        {payment.payment_status || "Draft"}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`pay-approval-badge ${getApprovalStatusClass(payment.approval_status)}`}
                      >
                        {payment.approval_status || "Pending"}
                      </span>
                    </td>
                    <td>
                      <div className="pay-action-group">
                        <button
                          className="pay-action-btn pay-action-btn-edit"
                          onClick={() => openEdit(payment)}
                          title="Edit"
                        >
                          <img
                            src={editIcon}
                            alt="Edit"
                            className="pay-action-icon"
                          />
                        </button>
                        {(payment.payment_status === "Draft" ||
                          payment.payment_status === "Submitted") && (
                          <button
                            className="pay-action-btn pay-action-btn-delete"
                            onClick={() => handleDelete(payment.id)}
                            title="Delete"
                          >
                            <img
                              src={deleteIcon}
                              alt="Delete"
                              className="pay-action-icon"
                            />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
//  ROOT — Payments Page with Approvals Tab
// ─────────────────────────────────────────────────────────
export default function Payments() {
  const [view, setView] = useState("list");

  return (
    <div className="pay-wrapper">
      {/* Tab Bar */}
      <div className="pay-tab-bar">
        <button
          className={`pay-tab-btn ${view === "list" ? "pay-tab-active" : ""}`}
          onClick={() => setView("list")}
        >
          💳 Payments
        </button>
        <button
          className={`pay-tab-btn ${view === "approvals" ? "pay-tab-active" : ""}`}
          onClick={() => setView("approvals")}
        >
          ✅ Approvals
        </button>
      </div>

      {view === "list" && <PaymentsView />}
      {view === "approvals" && <PaymentApprovals />}
    </div>
  );
}
